// src/components/Benefits/MisBeneficios.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  Card,
  Row,
  Col,
  Alert,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import ReviewModal from "../Rating/ReviewModal";
import BenefitCard from "../Home/BenefitCard";

// Helpers robustos
const isTrue = (v) => v === 1 || v === "1" || v === true;

// Detecta tipo de beneficio en base a los mismos campos que BenefitCard
const getBenefitTypeLabel = (b) => {
  const rawType = (b.discount_type || "").toString().toLowerCase().trim();
  const name = (b.benefit_name || b.name || "").toString().toLowerCase();

  const hasPercent =
    typeof b.discount_percentage === "number" && b.discount_percentage > 0;
  const hasFixed =
    typeof b.discount_amount === "number" && b.discount_amount > 0;

  // 2x1
  if (
    rawType.includes("two_for_one") ||
    rawType.includes("2x1") ||
    rawType.includes("2 x 1") ||
    name.includes("2x1") ||
    name.includes("2 x 1") ||
    isTrue(b.is_two_for_one) ||
    isTrue(b.two_for_one)
  ) {
    return "2x1";
  }

  // Fijo
  if (
    rawType.includes("fixed") ||
    rawType.includes("fijo") ||
    rawType.includes("monto") ||
    hasFixed
  ) {
    return "Descuento fijo";
  }

  // Porcentaje
  if (
    rawType.includes("percentage") ||
    rawType.includes("porcentaje") ||
    rawType.includes("%") ||
    hasPercent
  ) {
    return "% de descuento";
  }

  return null;
};

const MisBeneficios = () => {
  const [beneficios, setBeneficios] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI filtros
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | used | reviewed | not_reviewed
  const [showExpired, setShowExpired] = useState(true);

  // Orden y paginado
  const [sortBy, setSortBy] = useState("relevance"); // relevance | expiring | newest | oldest | discount
  const [pageSize, setPageSize] = useState(12);
  const [page, setPage] = useState(1);

  // Modal QR
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);

  // ReviewModal
  const [showReview, setShowReview] = useState(false);
  const [selectedClaimId, setSelectedClaimId] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // ===================== FETCH BENEFICIOS =====================
  const fetchBeneficios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/benefits/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBeneficios(res.data || []);
    } catch (err) {
      console.error("Error al traer beneficios:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBeneficios();
  }, [fetchBeneficios]);

  // ===================== HANDLERS =====================
  const handleVerQR = (beneficio) => {
    setSelected(beneficio);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
  };

  const handleOpenReview = (claimId) => {
    setSelectedClaimId(claimId);
    setShowReview(true);
  };

  const handleReviewSaved = () => {
    setShowReview(false);
    setSelectedClaimId(null);
    // Refrescar para ocultar botón Calificar si backend ya devuelve `reviewed`
    fetchBeneficios();
  };

  // ===================== NORMALIZACIÓN =====================
  const normalizeItem = (b) => {
    const validUntil = b.valid_until ? new Date(b.valid_until) : null;
    const createdAt = b.created_at ? new Date(b.created_at) : null;

    const raw = (b.status ?? "").toString().toLowerCase().trim();

    // Usado:
    const isUsed =
      raw === "used" ||
      raw === "completed" ||
      isTrue(b.status) ||
      !!b.used_at;

    // Pendiente:
    const isPending =
      (raw === "pending" ||
        raw === "0" ||
        b.status === 0 ||
        b.status === "0") && !isUsed;

    const expiredByFlag =
      typeof b.is_expired !== "undefined" ? isTrue(b.is_expired) : false;
    const expiredByDate = validUntil ? validUntil < new Date() : false;
    const isExpired = expiredByFlag || expiredByDate;

    // Reseñado: puede venir como booleano `reviewed` o `review_id`
    const reviewed =
      isTrue(b.reviewed) || !!b.review_id || !!b.review_id_claim;

    return {
      ...b,
      name: b.benefit_name || b.name, // aseguramos name para BenefitCard
      _validUntil: validUntil,
      _createdAt: createdAt,
      _isExpired: isExpired,
      _isPending: isPending,
      _isUsed: isUsed,
      _discount:
        typeof b.discount_percentage === "number"
          ? b.discount_percentage
          : 0,
      _reviewed: reviewed,
      _reviewRating: b.review_rating ?? null,
    };
  };

  const currency = (n) =>
    typeof n === "number"
      ? n.toLocaleString("es-AR", { style: "currency", currency: "ARS" })
      : null;

  const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString("es-AR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Dataset normalizado
  const data = useMemo(() => beneficios.map(normalizeItem), [beneficios]);

  // Ordenamiento base (pendientes primero)
  const sortBase = (a, b) => {
    if (a._isPending && !b._isPending) return -1;
    if (!a._isPending && b._isPending) return 1;
    return 0;
  };

  // Ordenamiento secundario según sortBy
  const sortSecondary = (a, b) => {
    switch (sortBy) {
      case "expiring": {
        const av = a._validUntil ? a._validUntil.getTime() : Infinity;
        const bv = b._validUntil ? b._validUntil.getTime() : Infinity;
        return av - bv;
      }
      case "newest": {
        const av = a._createdAt ? a._createdAt.getTime() : -Infinity;
        const bv = b._createdAt ? b._createdAt.getTime() : -Infinity;
        return bv - av; // más nuevo primero
      }
      case "oldest": {
        const av = a._createdAt ? a._createdAt.getTime() : Infinity;
        const bv = b._createdAt ? b._createdAt.getTime() : Infinity;
        return av - bv; // más antiguo primero
      }
      case "discount": {
        return (b._discount || 0) - (a._discount || 0);
      }
      default:
        return 0; // relevance => sólo sortBase
    }
  };

  // Filtros + orden
  const filtradosOrdenados = useMemo(() => {
    let list = [...data];

    // Buscar
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (b) =>
          b.benefit_name?.toLowerCase().includes(q) ||
          b.description?.toLowerCase().includes(q) ||
          b.business_name?.toLowerCase().includes(q)
      );
    }

    // Estado
    if (statusFilter !== "all") {
      if (statusFilter === "pending")
        list = list.filter((b) => b._isPending);
      else if (statusFilter === "used")
        list = list.filter((b) => b._isUsed);
      else if (statusFilter === "reviewed")
        list = list.filter((b) => b._reviewed);
      else if (statusFilter === "not_reviewed")
        list = list.filter((b) => !b._reviewed && b._isUsed);
    }

    // Vencidos
    if (!showExpired) {
      list = list.filter((b) => !b._isExpired);
    }

    // Orden (pendientes primero + criterio secundario)
    list.sort((a, b) => {
      const base = sortBase(a, b);
      if (base !== 0) return base;
      return sortSecondary(a, b);
    });

    return list;
  }, [data, query, statusFilter, showExpired, sortBy]);

  // Resetear página al cambiar filtros/orden
  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, showExpired, sortBy, pageSize]);

  // Paginado
  const total = filtradosOrdenados.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const pageItems = filtradosOrdenados.slice(start, start + pageSize);

  // ===================== RENDER =====================

  // 🧱 Skeleton loader mientras carga
  if (loading) {
    const skeletonItems = Array.from({ length: 8 });

    return (
      <div className="container mt-3">
        <h4 className="mb-3">Mis beneficios</h4>
        <Row xs={1} sm={2} md={3} lg={4} className="g-3">
          {skeletonItems.map((_, idx) => (
            <Col key={idx}>
              <Card className="h-100 shadow-sm">
                {/* Imagen skeleton */}
                <div
                  style={{
                    height: 160,
                    background: "#e5e7eb",
                  }}
                />
                <Card.Body className="d-flex flex-column">
                  {/* Título */}
                  <div
                    style={{
                      height: 14,
                      width: "70%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginBottom: 8,
                    }}
                  />
                  {/* Categoria / tipo */}
                  <div
                    style={{
                      height: 12,
                      width: "40%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginBottom: 12,
                    }}
                  />
                  {/* Descripción líneas */}
                  <div
                    style={{
                      height: 10,
                      width: "90%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  />
                  <div
                    style={{
                      height: 10,
                      width: "80%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  />
                  {/* Botones */}
                  <div className="mt-auto d-flex justify-content-between gap-2">
                    <div
                      style={{
                        height: 32,
                        flex: 1,
                        background: "#e5e7eb",
                        borderRadius: 6,
                      }}
                    />
                    <div
                      style={{
                        height: 32,
                        flex: 1,
                        background: "#e5e7eb",
                        borderRadius: 6,
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (!beneficios.length) {
    return (
      <div className="container mt-3">
        <Alert variant="info" className="text-center">
          Todavía no reclamaste ningún beneficio.
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mt-3">
      <h4 className="mb-3">Mis beneficios</h4>

      {/* Controles */}
      <Row className="gy-2 gx-2 align-items-end mb-3">
        <Col xs={12} md={4}>
          <Form.Label>Buscar</Form.Label>
          <Form.Control
            placeholder="Ej: 2x1 café, pizza, estética…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Col>

        <Col xs={12} md={3}>
          <Form.Label>Estado</Form.Label>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="pending">Pendientes</option>
            <option value="used">Usados</option>
            <option value="reviewed">Reseñados</option>
            <option value="not_reviewed">No reseñados</option>
          </Form.Select>
        </Col>

        <Col xs={12} md={3}>
          <Form.Label>Ordenar por</Form.Label>
          <Form.Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="relevance">Relevancia</option>
            <option value="expiring">Próximos a vencer</option>
            <option value="newest">Más nuevos</option>
            <option value="oldest">Más antiguos</option>
            <option value="discount">Mayor descuento</option>
          </Form.Select>
        </Col>

        <Col
          xs={6}
          md={2}
          className="d-flex align-items-center mt-3 mt-md-0"
        >
          <Form.Check
            type="switch"
            id="showExpired"
            label="Mostrar vencidos"
            checked={showExpired}
            onChange={(e) => setShowExpired(e.target.checked)}
          />
        </Col>

        <Col xs={6} md={2} className="mt-3 mt-md-0">
          <Form.Label>Tamaño de página</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={(e) =>
              setPageSize(parseInt(e.target.value || "12", 10))
            }
          >
            <option value={8}>8</option>
            <option value={12}>12</option>
            <option value={16}>16</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Grid */}
      {pageItems.length === 0 ? (
        <div className="text-center text-muted py-5">
          No encontramos beneficios con esos filtros.
        </div>
      ) : (
        <>
          <Row xs={1} sm={2} md={3} lg={4} className="g-3">
            {pageItems.map((b) => {
              const typeLabel = getBenefitTypeLabel(b);
              const expired = b._isExpired;

              const actions = (
                <div className="d-flex gap-2">
                  {/* Ver QR */}
                  <Button
                    variant="outline-primary"
                    className="flex-fill"
                    disabled={expired || b._isUsed}
                    onClick={() => handleVerQR(b)}
                  >
                    Ver QR
                  </Button>

                  {/* Segunda acción según estado */}
                  {b._isPending && !expired && (
                    <Button
                      className="flex-fill"
                      variant="primary"
                      disabled
                    >
                      Pendiente
                    </Button>
                  )}

                  {b._isUsed && !expired && (
                    b._reviewed ? (
                      <Button
                        className="flex-fill"
                        variant="secondary"
                        disabled
                      >
                        Reseñado
                      </Button>
                    ) : (
                      <Button
                        className="flex-fill"
                        variant="success"
                        onClick={() =>
                          handleOpenReview(b.claim_id ?? b.id)
                        }
                      >
                        Calificar
                      </Button>
                    )
                  )}

                  {!b._isPending && !b._isUsed && (
                    <Button
                      className="flex-fill"
                      variant="secondary"
                      disabled
                    >
                      No disponible
                    </Button>
                  )}
                </div>
              );

              return (
                <Col key={b.id}>
                  <BenefitCard
                    benefit={b}
                    comercio={
                      b.business_name
                        ? {
                            name: b.business_name,
                            slug: b.business_slug,
                          }
                        : undefined
                    }
                    showBusinessLink={!!b.business_slug}
                    // No pasamos onClaim aquí, usamos actions custom:
                    actions={actions}
                    typeLabel={typeLabel}
                  />
                </Col>
              );
            })}
          </Row>

          {/* Paginado */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
              Mostrando {start + 1}-
              {Math.min(start + pageSize, total)} de {total}
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                disabled={page <= 1}
                onClick={() =>
                  setPage((p) => Math.max(1, p - 1))
                }
              >
                ← Anterior
              </Button>
              <span className="align-self-center">
                Página {page} de {totalPages}
              </span>
              <Button
                variant="outline-secondary"
                disabled={page >= totalPages}
                onClick={() =>
                  setPage((p) => Math.min(totalPages, p + 1))
                }
              >
                Siguiente →
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Modal QR */}
      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Beneficio</Modal.Title>
        </Modal.Header>
        {selected && (
          <Modal.Body className="text-center">
            <h5 className="mb-3">{selected.benefit_name}</h5>

            {selected._isExpired || selected._isUsed ? (
              <p className="text-danger fw-bold">QR no disponible</p>
            ) : (
              <>
                <QRCodeCanvas value={selected.claim_code} size={220} />
                <p className="mt-3">
                  <strong>Código:</strong> {selected.claim_code}
                </p>
              </>
            )}

            <p className="mt-3">
              {selected.valid_until ? (
                selected._isExpired ? (
                  <span className="text-danger">Expirado</span>
                ) : (
                  <span className="text-success">
                    {selected.days_left != null
                      ? `Expira en ${selected.days_left} días`
                      : `Vence: ${formatDate(
                          selected.valid_until
                        )}`}
                  </span>
                )
              ) : (
                <span className="text-muted">Sin vencimiento</span>
              )}
            </p>
          </Modal.Body>
        )}
      </Modal>

      {/* Modal Review */}
      <ReviewModal
        show={showReview}
        onHide={() => setShowReview(false)}
        claimId={selectedClaimId}
        onSaved={handleReviewSaved}
      />
    </div>
  );
};

export default MisBeneficios;
